import type { QueryClient } from '@tanstack/react-query';
import { isAwaitingShippingFee, isVirtualOnlyOrder } from '@mlm/shared';
import { isAllVirtual, useCartStore } from '../storefront/lib/cart-store';
import { getTenantConfig } from './api';
import { UNPAID_DIALOG_SESSION_FLAG, unpaidOrdersQuery } from './lock-inputs';
import { safeInternalPath } from './post-auth';

/**
 * Where a fresh credentials login lands — the ONE-SHOT virtual-checkout resume.
 *
 * A visitor who started a virtual purchase (a course, a service) and got
 * interrupted at "create an account first" or "verify your payment" comes back,
 * logs in… and lands on the dashboard, with the thing they were buying nowhere
 * in sight. When the tenant flag `virtual_checkout_resume_on_login` is on, the
 * login submit resolves its destination here instead:
 *
 *   1. a payable unpaid VIRTUAL-ONLY order → its payment page (`/pay/:orderId`),
 *   2. else a non-empty all-virtual CHECKED cart → `/checkout`,
 *   3. else the validated `?next=` or `/dashboard` (the pre-existing behaviour).
 *
 * An explicit `?next=` on the login URL beats resume entirely — a deep link is a
 * *statement* of where the member wants to go; resume is only a better default.
 *
 * **This is a redirect, NOT a lock.** `RequireOrderSettled` (the hard unpaid-
 * order lock, flag `unpaid_order_lock_enabled`) re-asserts itself on every
 * navigation and will bounce a member BACK to the payment page until the order
 * settles. This module fires exactly once, at login submit, and never again for
 * the session: the member is free to navigate away from wherever it sends them.
 * The two are independent flags and must stay independent code paths — do not
 * fold this into the guard.
 *
 * A 'pay' resume also sets `UNPAID_DIALOG_SESSION_FLAG`: being sent to the
 * payment page IS the unpaid-orders prompt, so the soft dialog
 * (`UnpaidOrdersGate`) must not double-fire on top of it. `logout()` clears the
 * flag, so the next login re-arms both surfaces.
 */

export type LoginResumeDecision =
  | { kind: 'pay'; orderId: string }
  | { kind: 'checkout' }
  | { kind: 'none' };

/** The slice of an order row the decision reads. `metadata` is free-form jsonb
 *  — the shared predicates (`isVirtualOnlyOrder`, `isAwaitingShippingFee`) own
 *  the key names, so `OrderListRow` fits structurally. */
export type ResumeOrder = {
  id: string;
  metadata?: Record<string, unknown> | null;
};

/**
 * Pure decision — all inputs passed in, so the table tests need no network, no
 * store, no jsdom.
 *
 * `orders === undefined` means the unpaid-orders fetch FAILED, not "no unpaid
 * orders": the order branch yields nothing, but the cart branch must still be
 * consulted — the cart is local state and its answer is still trustworthy.
 */
export function decideLoginResume(input: {
  resumeEnabled: boolean;
  /** The (already validated) `?next=` from the login URL, or null. Non-null
   *  suppresses resume entirely. */
  explicitNext: string | null;
  /** Unpaid (`pending`/`to_pay`) orders, newest-first as `GET /orders` returns
   *  them; `undefined` when the fetch failed. */
  orders: ResumeOrder[] | undefined;
  cartLines: { isVirtual?: boolean; checked: boolean }[];
}): LoginResumeDecision {
  if (input.explicitNext !== null) return { kind: 'none' };
  if (!input.resumeEnabled) return { kind: 'none' };
  // A manual-fee order sits unpaid but CANNOT be paid yet — sending the member
  // to its pay page would show a screen with no pay button (same exclusion the
  // soft dialog makes). Physical/mixed orders are not a virtual checkout to
  // resume; they fall through to the cart branch.
  const payable = (input.orders ?? []).find(
    (o) => isVirtualOnlyOrder(o) && !isAwaitingShippingFee(o),
  );
  if (payable) return { kind: 'pay', orderId: payable.id };
  // Only CHECKED lines: checkout bills exactly the checked set, and
  // `isAllVirtual` already fails safe — empty selection or any line with an
  // unknown `isVirtual` reads as "not a virtual checkout" → no resume.
  if (isAllVirtual(input.cartLines.filter((l) => l.checked))) return { kind: 'checkout' };
  return { kind: 'none' };
}

/**
 * Effectful wrapper for the login submit: gathers the decision's inputs and
 * maps it to a navigable path.
 *
 * Uses `qc.fetchQuery` with the app's shared keys (`['tenant-config']`,
 * `unpaidOrdersQuery`) so both results land in the shared cache — the pages the
 * member lands on read the same entries and won't refetch. Every fetch failure
 * degrades to the pre-resume behaviour (`next`/dashboard); login must never
 * fail because a resume lookup did.
 */
export async function resolveLoginDestination(
  qc: QueryClient,
  rawNext: string | null,
): Promise<string> {
  // An invalid/off-site `?next=` is treated as ABSENT (it can't be navigated
  // to), so it neither wins nor suppresses resume.
  const explicitNext = safeInternalPath(rawNext);

  let resumeEnabled = false;
  try {
    const cfg = await qc.fetchQuery({ queryKey: ['tenant-config'], queryFn: getTenantConfig });
    resumeEnabled = cfg.virtualCheckoutResumeOnLogin === true;
  } catch {
    // Config unknown → flag off. Fail closed: no resume, normal landing.
  }

  // Skip the orders round-trip when the decision can't use it (explicit next or
  // flag off) — `decideLoginResume` returns 'none' in both cases regardless.
  const orders =
    resumeEnabled && explicitNext === null
      ? await qc.fetchQuery(unpaidOrdersQuery).catch(() => undefined)
      : undefined;

  const decision = decideLoginResume({
    resumeEnabled,
    explicitNext,
    orders,
    cartLines: Object.values(useCartStore.getState().lines),
  });

  switch (decision.kind) {
    case 'pay':
      // The redirect IS the unpaid-orders prompt — suppress the soft dialog for
      // this session so it doesn't double-fire on the very order we resumed.
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(UNPAID_DIALOG_SESSION_FLAG, '1');
      }
      return `/pay/${decision.orderId}`;
    case 'checkout':
      return '/checkout';
    case 'none':
      return explicitNext ?? '/dashboard';
  }
}
